import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import moment from 'moment/moment';

import ThreeLevelSteps from '../../../common/steps';
import AdditionalOptions from './common/additional_options';
import AddProductsDialog from '../../../common/warehouse_and_food_partner_order_form/add_products_dialog';
import ProductsList from './common/products_list';
import { getFoodPartnerNotificationData } from '../../../../../core/utils/notification_utils';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../../../core/constants';
import useNotification from '../../../../../core/hooks/use_notification';
import { getFoodPartnerAction } from '../../../../../features/food_partner/get_food_partner';
import SectionPaper from '../../../../common/section_paper';
import BottomActionBar from '../../../../common/bottom_action_bar';
import { Button, SubmitButton } from '../../../../common/button';

const PZForm = ({ form, setView, data, setData }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    useEffect(() => {
        dispatch(getFoodPartnerAction(form?.foodPartnerId))
            .then(response => showErrorMessage(getFoodPartnerNotificationData(response)));
    }, [dispatch, form, showErrorMessage]);

    const initialValues = {
        productsList: data?.formik?.productsList || [],
        attachments: data?.formik?.attachments || [],
    };

    const onReturn = () => {
        setData({});
        setView(0);
    };

    const onChangeSetDate = (newDate, allDates, formik) => {
        const productsList = formik?.values?.productsList[0]?.map(item => {
            if (!allDates && item?.expirationDate) {
                return item;
            }

            return ({ ...item, expirationDate: newDate });
        });

        formik.setFieldValue(`productsList.${ 0 }`, productsList);
    };

    const onCopyHistoricalDocument = (values, formik) => {
        const productsList = [];

        values?.forEach((product, index) => {
            productsList.push({
                position: index,
                name: product?.productName,
                productId: product?.productId,
                productBatchId: '',
                quantity: product?.quantity,
                expirationDate: product?.defaultDaysToExpiration ? moment(form?.deliveryDate).add(Math.max(product?.defaultDaysToExpiration - 1, 0), 'days') : '',
                price: product?.purchaseNetPrice || '',
                type: product?.type
            });
        });

        formik.setFieldValue(`productsList.${ 0 }`, productsList);
    };

    const handleSubmit = (values) => {
        const documents = [{
            documentProducts: [
                ...values?.productsList?.[0]?.map(item => ({
                    productId: item?.productId,
                    quantity: item?.quantity,
                    expirationDate: item?.expirationDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
                    price: item?.price
                }))
            ]
        }];

        setData({
            form: {
                ...form,
                deliveryDate: form?.deliveryDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
                documents,
                attachments: values?.attachments || []
            },
            formik: values
        });
        setView(5);
    };

    const validate = (values) => {
        const errors = {};
        const productsListErrors = [];
        let hasErrors = false;

        if (!values.productsList || values.productsList.length === 0) {
            errors.productsList = ['Wybierz minimum 1 produkt'];
            return errors;
        }

        values.productsList.forEach((products, index) => {
            if (!products || products.length === 0) {
                productsListErrors[index] = 'Wybierz minimum 1 produkt';
                hasErrors = true;
            } else {
                const itemErrorsArray = [];
                products.forEach((item, itemIndex) => {
                    const itemErrors = {};

                    if (!item?.quantity) {
                        itemErrors.quantity = 'Pole wymagane';
                    } else if (item?.quantity <= 0) {
                        itemErrors.quantity = 'Proszę podać wartość całkowitą dodatnią';
                    }

                    if (!item?.expirationDate) {
                        itemErrors.expirationDate = 'Pole wymagane';
                    }

                    if (!item?.price) {
                        itemErrors.price = 'Pole wymagane';
                    } else if (item?.price <= 0) {
                        itemErrors.price = 'Proszę podać wartość dodatnią';
                    }

                    if (Object.keys(itemErrors).length > 0) {
                        itemErrorsArray[itemIndex] = itemErrors;
                        hasErrors = true;
                    }
                });

                if (itemErrorsArray.length > 0 || productsListErrors[index]) {
                    productsListErrors[index] = productsListErrors[index] || itemErrorsArray;
                }
            }
        });

        if (hasErrors) {
            errors.productsList = productsListErrors;
        }

        return errors;
    };

    return (
        <Formik
            initialValues={ initialValues }
            validate={ validate }
            onSubmit={ handleSubmit }
        >
            { (formik) => (
                <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
                    <Box sx={ { flexGrow: 1, pb: 4 } }>
                        <Stack spacing={ 3 }>
                            <ThreeLevelSteps secondStep/>
                            <AdditionalOptions
                                onChangeSetDate={ (newDate, allDates) => onChangeSetDate(newDate, allDates, formik) }
                                form={ form }
                                onCopyHistoricalDocument={ (values) => onCopyHistoricalDocument(values, formik) }
                            />

                            <SectionPaper title="Magazyn Główny">
                                <Stack spacing={ 2 }>
                                    <ProductsList
                                        formik={ formik }
                                        productsListIndex={ 0 }
                                    />
                                    <AddProductsDialog
                                        foodPartnerId={ form?.foodPartnerId }
                                        formik={ formik }
                                        dateToCalculateExpirationDate={ form?.deliveryDate }
                                        productsListIndex={ 0 }
                                    />
                                </Stack>
                            </SectionPaper>
                        </Stack>
                    </Box>

                    <BottomActionBar>
                        <Box sx={ { display: 'flex', justifyContent: 'space-between', width: '100%' } }>
                            <Button variant="outlined" onClick={ onReturn }>Wróć</Button>
                            <SubmitButton type="button" onClick={ formik.submitForm }>Dalej</SubmitButton>
                        </Box>
                    </BottomActionBar>
                </Box>
            ) }
        </Formik>
    );
};

export default PZForm;